import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Swords, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Modal, Field, TextInput, Toggle, PrimaryButton } from "@/components/admin/ui";

const empty = { player1: "", player1_id: "", player2: "", player2_id: "", battle_date: "", battle_time: "", active: true, order: 0 };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (d) => {
  if (!d) return "TBA";
  const [y, m, day] = d.split("-");
  if (!m || !day) return d;
  return `${Number(day)} ${MONTHS[Number(m) - 1]} ${y}`;
};

export default function ManagePkBattles() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const { data: items } = useQuery({ queryKey: ["admin-pk-battles"], queryFn: async () => (await api.get("/admin/pk-battles")).data });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-pk-battles"] });
    qc.invalidateQueries({ queryKey: ["pk-battles"] });
  };

  const save = useMutation({
    mutationFn: async (data) => editing ? api.put(`/admin/pk-battles/${editing}`, data) : api.post("/admin/pk-battles", data),
    onSuccess: () => { invalidate(); setModal(false); toast.success(editing ? "Battle updated" : "Battle added"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save"),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/pk-battles/${id}`),
    onSuccess: () => { invalidate(); toast.success("Battle deleted"); },
  });

  const openAdd = () => { setForm({ ...empty, order: (items?.length || 0) }); setEditing(null); setModal(true); };
  const openEdit = (b) => { setForm(b); setEditing(b.id); setModal(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">PK Battles</h1>
          <p className="mt-1 text-slate-400">Schedule upcoming PK battles shown on the website.</p>
        </div>
        <PrimaryButton onClick={openAdd} testid="add-pk-btn"><Plus className="h-5 w-5" /> Add Battle</PrimaryButton>
      </div>

      <div className="mt-8 space-y-3">
        {(items || []).map((b) => (
          <div key={b.id} className="card-surface flex items-center gap-4 rounded-xl p-4" data-testid={`admin-pk-${b.id}`}>
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl gold-gradient"><Swords className="h-5 w-5 text-obsidian" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-bold text-slate-100">{b.player1} <span className="gold-text">vs</span> {b.player2}</p>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${b.active ? "bg-whatsapp/15 text-whatsapp" : "bg-slate-600/30 text-slate-400"}`}>{b.active ? "Active" : "Inactive"}</span>
              </div>
              <p className="mt-1 text-sm text-slate-400">
                <Calendar className="mr-1 inline h-3.5 w-3.5 text-gold" />{fmtDate(b.battle_date)} · <Clock className="mr-1 inline h-3.5 w-3.5 text-gold" />{b.battle_time || "TBA"}
                {(b.player1_id || b.player2_id) && (
                  <span className="ml-2 font-mono text-xs text-slate-500">IDs: {b.player1_id || "—"} / {b.player2_id || "—"}</span>
                )}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button onClick={() => openEdit(b)} className="rounded-lg p-2 text-gold hover:bg-gold/10" data-testid={`edit-pk-${b.id}`}><Pencil className="h-5 w-5" /></button>
              <button onClick={() => { if (window.confirm("Delete this battle?")) del.mutate(b.id); }} className="rounded-lg p-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-pk-${b.id}`}><Trash2 className="h-5 w-5" /></button>
            </div>
          </div>
        ))}
        {items?.length === 0 && <p className="py-12 text-center text-slate-500">No battles yet. Add your first PK battle.</p>}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit PK Battle" : "Add PK Battle"} testid="pk-modal">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Player 1 Name"><TextInput value={form.player1} onChange={(e) => setForm((f) => ({ ...f, player1: e.target.value }))} required testid="pk-p1-input" /></Field>
            <Field label="Player 2 Name"><TextInput value={form.player2} onChange={(e) => setForm((f) => ({ ...f, player2: e.target.value }))} required testid="pk-p2-input" /></Field>
            <Field label="Player 1 ID"><TextInput value={form.player1_id} onChange={(e) => setForm((f) => ({ ...f, player1_id: e.target.value }))} placeholder="e.g. 100123" testid="pk-p1id-input" /></Field>
            <Field label="Player 2 ID"><TextInput value={form.player2_id} onChange={(e) => setForm((f) => ({ ...f, player2_id: e.target.value }))} placeholder="e.g. 100456" testid="pk-p2id-input" /></Field>
            <Field label="Date"><TextInput type="date" value={form.battle_date} onChange={(e) => setForm((f) => ({ ...f, battle_date: e.target.value }))} testid="pk-date-input" /></Field>
            <Field label="Time"><TextInput value={form.battle_time} onChange={(e) => setForm((f) => ({ ...f, battle_time: e.target.value }))} placeholder="e.g. 8:00 PM" testid="pk-time-input" /></Field>
          </div>
          <div className="grid grid-cols-2 items-center gap-3">
            <Field label="Display Order"><TextInput type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} testid="pk-order-input" /></Field>
            <div className="pt-6">
              <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} label={form.active ? "Active" : "Inactive"} testid="pk-active-toggle" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Cancel</button>
            <PrimaryButton type="submit" disabled={save.isPending} testid="save-pk-btn">{save.isPending ? "Saving..." : "Save Battle"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
